<template>
    <div class="my-5 flex flex-col gap-5">
        <header class="flex flex-wrap items-end justify-between gap-4">
            <div class="min-w-0">
                <div class="flex items-center gap-2">
                    <h1
                        class="text-xl font-semibold tracking-tight text-slate-900 dark:text-white"
                    >
                        Iris
                    </h1>
                    <span
                        class="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20"
                    >
                        <chat-bubble-left-right-icon class="h-3.5 w-3.5" />
                        WhatsApp
                    </span>
                </div>
                <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Lembretes de PIX pendente e quanto eles trazem de volta.
                </p>
            </div>

            <div
                v-if="tab !== 'settings'"
                class="flex flex-wrap items-center gap-3"
            >
                <span
                    v-if="metrics"
                    class="hidden text-xs text-slate-400 sm:inline dark:text-slate-500"
                >
                    Atualizado às {{ generatedTime }}
                </span>
                <div
                    class="inline-flex rounded-lg bg-slate-100 p-0.5 dark:bg-white/5"
                    role="group"
                    aria-label="Período"
                >
                    <button
                        v-for="period in periods"
                        :key="period"
                        type="button"
                        @click="changePeriod(period)"
                        :disabled="metricsLoading"
                        :aria-pressed="days === period"
                        class="rounded-md px-3 py-1 text-sm font-medium transition disabled:cursor-wait"
                        :class="
                            days === period
                                ? 'bg-white text-slate-900 shadow-xs dark:bg-white/15 dark:text-white'
                                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                        "
                    >
                        {{ period }} dias
                    </button>
                </div>
                <button
                    type="button"
                    @click="fetchMetrics(true)"
                    :disabled="metricsLoading"
                    v-tooltip="
                        'Recalcular agora (o resumo fica 5 min em cache)'
                    "
                    aria-label="Atualizar métricas"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 disabled:cursor-wait dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-slate-200"
                >
                    <arrow-path-icon
                        class="h-4 w-4"
                        :class="{ 'animate-spin': metricsLoading }"
                    />
                </button>
            </div>
        </header>

        <nav
            class="flex gap-6 overflow-x-auto border-b border-slate-200 dark:border-white/10"
            aria-label="Seções do Iris"
        >
            <button
                v-for="item in tabs"
                :key="item.id"
                type="button"
                @click="selectTab(item.id)"
                :aria-current="tab === item.id ? 'page' : undefined"
                class="-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 py-2.5 text-sm font-medium transition-colors"
                :class="
                    tab === item.id
                        ? 'border-indigo-500 text-slate-900 dark:text-white'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                "
            >
                <component :is="item.icon" class="h-4 w-4" />
                {{ item.label }}
                <span
                    v-if="metrics && tabCount(item.id) !== null"
                    class="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300"
                >
                    {{ formatNumber(tabCount(item.id)) }}
                </span>
                <span
                    v-if="item.id === 'settings' && disabledNotificationCount"
                    class="h-2 w-2 rounded-full bg-amber-500"
                    v-tooltip="notificationsStatusLabel"
                ></span>
            </button>
        </nav>

        <div
            v-if="metricsError && tab !== 'settings'"
            class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
        >
            <span class="flex items-center gap-2">
                <exclamation-triangle-icon class="h-4 w-4 shrink-0" />
                {{ metricsError }}
            </span>
            <button
                type="button"
                @click="fetchMetrics(true)"
                class="font-semibold underline"
            >
                Tentar de novo
            </button>
        </div>

        <section v-show="tab === 'overview'" aria-label="Visão geral">
            <div
                v-if="!metrics && metricsLoading"
                class="grid gap-4 lg:grid-cols-3"
            >
                <div
                    v-for="block in skeletonBlocks"
                    :key="block"
                    :class="[panel, block, 'animate-pulse']"
                >
                    <div
                        class="h-3 w-32 rounded bg-slate-100 dark:bg-white/10"
                    ></div>
                    <div
                        class="mt-4 h-8 w-48 rounded bg-slate-100 dark:bg-white/10"
                    ></div>
                </div>
            </div>

            <div v-else-if="metrics && !hasActivity" :class="panel">
                <inline-empty-state
                    :icon="emptyIcon"
                    :title="`Nenhum disparo do Iris nos últimos ${days} dias`"
                    hint="Os números aparecem aqui assim que o Payments enviar o primeiro Pix Gerado."
                />
            </div>

            <div
                v-else-if="metrics"
                class="flex flex-col gap-4 transition-opacity"
                :class="{ 'opacity-60': metricsLoading }"
            >
                <div class="grid gap-4 lg:grid-cols-3">
                    <div
                        :class="[
                            panel,
                            'flex flex-col justify-between gap-6 lg:col-span-2',
                        ]"
                    >
                        <div>
                            <p
                                class="text-sm font-medium text-slate-500 dark:text-slate-400"
                            >
                                Valor recuperado
                                <span
                                    class="text-slate-400 dark:text-slate-500"
                                >
                                    · últimos {{ days }} dias
                                </span>
                            </p>
                            <p
                                class="mt-2 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl dark:text-white"
                            >
                                {{ formatMoney(conversion.recovered_amount) }}
                            </p>
                            <p
                                class="mt-2 text-sm text-slate-500 dark:text-slate-400"
                            >
                                <template v-if="canSeeMoney">
                                    de
                                    {{
                                        formatMoney(conversion.reminded_amount)
                                    }}
                                    em aberto nos pedidos que receberam o
                                    lembrete
                                </template>
                                <template v-else>
                                    Valores visíveis só para roles financeiras.
                                </template>
                            </p>
                            <div
                                v-if="canSeeMoney"
                                class="mt-4 flex items-center gap-3"
                            >
                                <div
                                    class="h-2 flex-1 overflow-hidden rounded-full bg-indigo-100 dark:bg-indigo-500/15"
                                    role="meter"
                                    aria-label="Parte do valor em aberto que voltou"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    :aria-valuenow="conversion.amount_rate"
                                >
                                    <div
                                        class="h-full rounded-full bg-indigo-500 dark:bg-indigo-400"
                                        :style="{
                                            width: `${clampPercent(conversion.amount_rate)}%`,
                                        }"
                                    ></div>
                                </div>
                                <span
                                    class="text-sm font-semibold text-slate-700 dark:text-slate-200"
                                >
                                    {{ formatPercent(conversion.amount_rate) }}
                                </span>
                            </div>
                        </div>

                        <dl
                            class="grid grid-cols-1 gap-4 divide-slate-100 border-t border-slate-100 pt-4 sm:grid-cols-3 sm:gap-0 sm:divide-x dark:divide-white/5 dark:border-white/5"
                        >
                            <div
                                v-for="stat in heroStats"
                                :key="stat.label"
                                class="sm:px-5 sm:first:pl-0"
                            >
                                <dt
                                    class="text-xs font-medium text-slate-500 dark:text-slate-400"
                                >
                                    {{ stat.label }}
                                </dt>
                                <dd
                                    class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
                                >
                                    {{ stat.value }}
                                </dd>
                                <dd
                                    class="mt-0.5 text-xs text-slate-400 dark:text-slate-500"
                                >
                                    {{ stat.hint }}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div :class="panel">
                        <h2
                            class="text-sm font-semibold text-slate-900 dark:text-white"
                        >
                            Do PIX ao pagamento
                        </h2>
                        <p
                            class="mt-0.5 text-xs text-slate-500 dark:text-slate-400"
                        >
                            pedidos no período
                        </p>
                        <ol class="mt-5 flex flex-col gap-5">
                            <li
                                v-for="(stage, index) in funnel"
                                :key="stage.key"
                            >
                                <div
                                    class="flex items-baseline justify-between gap-2 text-sm"
                                >
                                    <span
                                        class="text-slate-600 dark:text-slate-300"
                                    >
                                        {{ stage.label }}
                                    </span>
                                    <span
                                        class="font-semibold tabular-nums text-slate-900 dark:text-white"
                                    >
                                        {{ formatNumber(stage.value) }}
                                    </span>
                                </div>
                                <div
                                    class="mt-1.5 h-2 rounded-r"
                                    :style="{
                                        width: `${stage.width}%`,
                                        minWidth: stage.value ? '4px' : '0',
                                        backgroundColor: funnelColors[index],
                                    }"
                                ></div>
                                <p
                                    v-if="stage.note"
                                    class="mt-1 text-xs text-slate-400 dark:text-slate-500"
                                >
                                    {{ stage.note }}
                                </p>
                            </li>
                        </ol>
                    </div>
                </div>

                <div class="grid gap-4 lg:grid-cols-3">
                    <div :class="[panel, 'lg:col-span-2']">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <h2
                                    class="text-sm font-semibold text-slate-900 dark:text-white"
                                >
                                    {{
                                        canSeeMoney
                                            ? "Recuperado por dia"
                                            : "Pedidos recuperados por dia"
                                    }}
                                </h2>
                                <p
                                    class="mt-0.5 text-xs text-slate-500 dark:text-slate-400"
                                >
                                    pela data em que o pagamento foi aprovado
                                </p>
                            </div>
                            <button
                                type="button"
                                @click="showDailyTable = !showDailyTable"
                                class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-slate-200"
                            >
                                <component
                                    :is="showDailyTable ? chartIcon : tableIcon"
                                    class="h-4 w-4"
                                />
                                {{ showDailyTable ? "Gráfico" : "Tabela" }}
                            </button>
                        </div>

                        <div
                            v-if="showDailyTable"
                            class="mt-3 max-h-[330px] overflow-y-auto"
                        >
                            <table class="w-full text-sm">
                                <thead
                                    class="sticky top-0 bg-white text-left text-[11px] uppercase tracking-wider text-slate-400 dark:bg-[#110f19]"
                                >
                                    <tr>
                                        <th class="py-2 font-semibold">Dia</th>
                                        <th
                                            class="py-2 text-right font-semibold"
                                        >
                                            Lembretes
                                        </th>
                                        <th
                                            class="py-2 text-right font-semibold"
                                        >
                                            Recuperados
                                        </th>
                                        <th
                                            v-if="canSeeMoney"
                                            class="py-2 text-right font-semibold"
                                        >
                                            Valor
                                        </th>
                                    </tr>
                                </thead>
                                <tbody
                                    class="divide-y divide-slate-100 tabular-nums dark:divide-white/5"
                                >
                                    <tr
                                        v-for="point in dailyDesc"
                                        :key="point.date"
                                        class="text-slate-600 dark:text-slate-300"
                                    >
                                        <td class="py-1.5">
                                            {{ point.label }}
                                        </td>
                                        <td class="py-1.5 text-right">
                                            {{ formatNumber(point.reminded) }}
                                        </td>
                                        <td class="py-1.5 text-right">
                                            {{ formatNumber(point.recovered) }}
                                        </td>
                                        <td
                                            v-if="canSeeMoney"
                                            class="py-1.5 text-right font-medium text-slate-900 dark:text-white"
                                        >
                                            {{
                                                formatMoney(
                                                    point.recovered_amount,
                                                )
                                            }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <highcharts
                            v-else
                            :key="chartKey"
                            :options="dailyChart"
                            class="mt-3"
                        />
                    </div>

                    <div class="flex flex-col gap-4">
                        <div :class="panel">
                            <h2
                                class="text-sm font-semibold text-slate-900 dark:text-white"
                            >
                                Como pagaram
                            </h2>
                            <p
                                class="mt-0.5 text-xs text-slate-500 dark:text-slate-400"
                            >
                                forma de pagamento da transação recuperada
                            </p>
                            <template v-if="paymentMix.length">
                                <div
                                    class="mt-4 flex h-3 gap-0.5"
                                    role="img"
                                    :aria-label="paymentMixLabel"
                                >
                                    <div
                                        v-for="item in paymentMix"
                                        :key="item.key"
                                        class="h-full first:rounded-l last:rounded-r"
                                        :style="{
                                            flexGrow: item.orders,
                                            flexBasis: 0,
                                            minWidth: '4px',
                                            backgroundColor: item.color,
                                        }"
                                        v-tooltip="
                                            `${item.label}: ${formatPercent(item.share)}`
                                        "
                                    ></div>
                                </div>
                                <ul class="mt-4 flex flex-col gap-3">
                                    <li
                                        v-for="item in paymentMix"
                                        :key="item.key"
                                        class="flex items-start justify-between gap-3 text-sm"
                                    >
                                        <span
                                            class="flex items-center gap-2 text-slate-600 dark:text-slate-300"
                                        >
                                            <span
                                                class="h-2.5 w-2.5 shrink-0 rounded-sm"
                                                :style="{
                                                    backgroundColor: item.color,
                                                }"
                                            ></span>
                                            {{ item.label }}
                                        </span>
                                        <span class="text-right tabular-nums">
                                            <span
                                                class="font-semibold text-slate-900 dark:text-white"
                                            >
                                                {{ formatNumber(item.orders) }}
                                            </span>
                                            <span class="text-slate-400">
                                                ·
                                                {{ formatPercent(item.share) }}
                                            </span>
                                            <span
                                                class="block text-xs text-slate-400 dark:text-slate-500"
                                            >
                                                {{ formatMoney(item.amount) }}
                                            </span>
                                        </span>
                                    </li>
                                </ul>
                            </template>
                            <p
                                v-else
                                class="mt-4 text-sm text-slate-400 dark:text-slate-500"
                            >
                                Nenhum pedido recuperado no período.
                            </p>
                        </div>

                        <div :class="panel">
                            <div
                                class="flex items-center justify-between gap-2"
                            >
                                <h2
                                    class="text-sm font-semibold text-slate-900 dark:text-white"
                                >
                                    Envios
                                </h2>
                                <span
                                    class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                                    :class="deliveryStatus.classes"
                                >
                                    <component
                                        :is="deliveryStatus.icon"
                                        class="h-3.5 w-3.5"
                                    />
                                    {{ deliveryStatus.label }}
                                </span>
                            </div>
                            <dl
                                class="mt-2 divide-y divide-slate-100 text-sm dark:divide-white/5"
                            >
                                <div
                                    v-for="row in deliveryRows"
                                    :key="row.label"
                                    class="flex items-center justify-between gap-3 py-2.5"
                                >
                                    <dt class="min-w-0">
                                        <span
                                            class="text-slate-700 dark:text-slate-200"
                                        >
                                            {{ row.label }}
                                        </span>
                                        <span
                                            class="block truncate text-xs text-slate-400 dark:text-slate-500"
                                        >
                                            {{
                                                row.last
                                                    ? `último ${row.last}`
                                                    : "sem envios no período"
                                            }}
                                        </span>
                                    </dt>
                                    <dd class="text-right tabular-nums">
                                        <span
                                            class="font-semibold text-slate-900 dark:text-white"
                                        >
                                            {{ formatNumber(row.sent) }}
                                        </span>
                                        <span
                                            class="block text-xs"
                                            :class="
                                                row.failed
                                                    ? 'text-amber-600 dark:text-amber-400'
                                                    : 'text-slate-400 dark:text-slate-500'
                                            "
                                        >
                                            {{
                                                row.failed
                                                    ? `${formatNumber(row.failed)} com falha`
                                                    : "sem falhas"
                                            }}
                                        </span>
                                    </dd>
                                </div>
                            </dl>
                            <button
                                type="button"
                                @click="selectTab('settings')"
                                class="mt-2 flex w-full items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-left text-xs text-slate-500 transition hover:bg-slate-100 dark:bg-white/5 dark:text-slate-400 dark:hover:bg-white/10"
                            >
                                <span>
                                    Lembrete {{ effectiveDelay }} min após o PIX
                                    · {{ enabledNotificationCount }}/{{
                                        notificationTypes.length
                                    }}
                                    notificações ligadas
                                </span>
                                <chevron-right-icon class="h-4 w-4 shrink-0" />
                            </button>
                        </div>
                    </div>
                </div>

                <div class="grid gap-4 lg:grid-cols-2">
                    <div :class="panel">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <h2
                                    class="text-sm font-semibold text-slate-900 dark:text-white"
                                >
                                    Sellers com mais recuperação
                                </h2>
                                <p
                                    class="mt-0.5 text-xs text-slate-500 dark:text-slate-400"
                                >
                                    {{
                                        sellers.total === 1
                                            ? "1 seller teve vendas recuperadas"
                                            : `${formatNumber(sellers.total)} sellers tiveram vendas recuperadas`
                                    }}
                                </p>
                            </div>
                            <button
                                v-if="sellers.total"
                                type="button"
                                @click="selectTab('sellers')"
                                class="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-300"
                            >
                                Ver todos
                                <chevron-right-icon class="h-4 w-4" />
                            </button>
                        </div>

                        <div
                            v-if="sellerHighlight"
                            class="mt-4 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 p-4 text-white shadow-sm"
                        >
                            <div class="flex items-center gap-3">
                                <sparkles-icon
                                    class="h-5 w-5 shrink-0 text-indigo-100"
                                />
                                <div class="min-w-0 flex-1">
                                    <p
                                        class="text-[11px] font-semibold uppercase tracking-wider text-indigo-100"
                                    >
                                        Destaque do período
                                    </p>
                                    <p
                                        class="mt-0.5 truncate text-sm font-semibold"
                                    >
                                        {{ sellerHighlight.producer.name }}
                                    </p>
                                    <p class="text-xs text-indigo-100">
                                        {{
                                            formatNumber(
                                                sellerHighlight.recovered_orders,
                                            )
                                        }}
                                        de
                                        {{
                                            formatNumber(
                                                sellerHighlight.sales_orders,
                                            )
                                        }}
                                        vendas vieram do lembrete
                                    </p>
                                </div>
                                <p
                                    class="text-3xl font-semibold tracking-tight"
                                >
                                    {{
                                        formatPercent(
                                            sellerHighlight.sales_share,
                                            0,
                                        )
                                    }}
                                </p>
                            </div>
                        </div>

                        <ul
                            v-if="sellersTop.length"
                            class="mt-2 divide-y divide-slate-100 dark:divide-white/5"
                        >
                            <li
                                v-for="seller in sellersTop"
                                :key="seller.producer.id"
                            >
                                <button
                                    type="button"
                                    @click="showSellerRecoveries(seller)"
                                    v-tooltip="
                                        'Ver os pedidos recuperados deste seller'
                                    "
                                    class="group flex w-full items-center gap-3 py-3 text-left"
                                >
                                    <span
                                        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
                                    >
                                        {{ initials(seller.producer.name) }}
                                    </span>
                                    <div class="min-w-0 flex-1">
                                        <div
                                            class="flex items-baseline justify-between gap-2"
                                        >
                                            <span
                                                class="truncate text-sm font-medium text-slate-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-300"
                                            >
                                                {{ seller.producer.name }}
                                            </span>
                                            <span
                                                class="shrink-0 text-sm font-semibold tabular-nums text-slate-900 dark:text-white"
                                            >
                                                {{
                                                    formatMoney(
                                                        seller.recovered_amount,
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div
                                            class="mt-1.5 flex items-center gap-2"
                                        >
                                            <div
                                                class="h-1.5 flex-1 overflow-hidden rounded-full bg-indigo-100 dark:bg-indigo-500/15"
                                            >
                                                <div
                                                    class="h-full rounded-full bg-indigo-500 dark:bg-indigo-400"
                                                    :style="{
                                                        width: `${clampPercent(seller.sales_share)}%`,
                                                    }"
                                                ></div>
                                            </div>
                                            <span
                                                class="w-12 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-200"
                                            >
                                                {{
                                                    formatPercent(
                                                        seller.sales_share,
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <p
                                            class="mt-1 text-xs text-slate-400 dark:text-slate-500"
                                        >
                                            {{
                                                formatNumber(
                                                    seller.recovered_orders,
                                                )
                                            }}
                                            de
                                            {{
                                                formatNumber(
                                                    seller.sales_orders,
                                                )
                                            }}
                                            vendas vieram do lembrete
                                        </p>
                                    </div>
                                </button>
                            </li>
                        </ul>
                        <p
                            v-else
                            class="mt-3 text-sm text-slate-400 dark:text-slate-500"
                        >
                            Nenhum seller com venda recuperada no período.
                        </p>
                    </div>

                    <div :class="panel">
                        <div class="flex items-center justify-between gap-3">
                            <h2
                                class="text-sm font-semibold text-slate-900 dark:text-white"
                            >
                                Últimas recuperações
                            </h2>
                            <button
                                v-if="conversion.paid_after_reminder > 0"
                                type="button"
                                @click="selectTab('recoveries')"
                                class="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-300"
                            >
                                Ver todas ({{
                                    formatNumber(
                                        conversion.paid_after_reminder,
                                    )
                                }})
                                <chevron-right-icon class="h-4 w-4" />
                            </button>
                        </div>
                        <div
                            v-if="latest.loading && !latest.rows.length"
                            class="mt-3 flex flex-col gap-3"
                        >
                            <div
                                v-for="n in 3"
                                :key="n"
                                class="h-10 animate-pulse rounded bg-slate-100 dark:bg-white/5"
                            ></div>
                        </div>
                        <ul
                            v-else-if="latest.rows.length"
                            class="mt-1 divide-y divide-slate-100 dark:divide-white/5"
                        >
                            <li
                                v-for="row in latest.rows"
                                :key="row.transaction.id"
                                class="flex items-center gap-4 py-3"
                            >
                                <span
                                    class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400"
                                >
                                    <payment-method-icon
                                        :method="row.transaction.payment_method"
                                        :hide-name="true"
                                    />
                                </span>
                                <div class="min-w-0 flex-1">
                                    <p
                                        class="truncate text-sm font-medium text-slate-900 dark:text-white"
                                    >
                                        {{
                                            row.order.customer_name ||
                                            "Cliente sem nome"
                                        }}
                                    </p>
                                    <p
                                        class="truncate text-xs text-slate-400 dark:text-slate-500"
                                    >
                                        {{
                                            row.order.product ||
                                            "Produto não informado"
                                        }}
                                        · pagou
                                        {{ formatMinutes(row.minutes_to_pay) }}
                                        após o lembrete
                                    </p>
                                </div>
                                <div class="text-right">
                                    <p
                                        class="text-sm font-semibold tabular-nums text-slate-900 dark:text-white"
                                    >
                                        {{ formatMoney(row.recovered_amount) }}
                                    </p>
                                    <p
                                        class="text-xs text-slate-400 dark:text-slate-500"
                                    >
                                        {{ row.paid_at }}
                                    </p>
                                </div>
                                <router-link
                                    :to="orderRoute(row)"
                                    target="_blank"
                                    v-tooltip="'Ver pedido'"
                                    :aria-label="`Ver pedido ${row.order.id}`"
                                    class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-slate-200"
                                >
                                    <eye-icon class="h-4 w-4" />
                                </router-link>
                            </li>
                        </ul>
                        <p
                            v-else
                            class="mt-3 text-sm text-slate-400 dark:text-slate-500"
                        >
                            Nenhum pedido recuperado no período.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <section v-show="tab === 'sellers'" aria-label="Sellers">
            <div v-if="!sellers.items.length" :class="panel">
                <inline-empty-state
                    :icon="usersIcon"
                    :title="`Nenhum seller com venda recuperada nos últimos ${days} dias`"
                    hint="Quando um comprador pagar depois do lembrete, o seller aparece aqui com a parte das vendas que voltou."
                />
            </div>
            <template v-else>
                <p class="mb-3 text-sm text-slate-500 dark:text-slate-400">
                    Recuperamos vendas de
                    <strong
                        class="font-semibold text-slate-900 dark:text-white"
                    >
                        {{ formatNumber(sellers.total) }}
                    </strong>
                    {{ sellers.total === 1 ? "seller" : "sellers" }} nos últimos
                    {{ days }} dias
                    <template v-if="sellers.total > sellers.items.length">
                        · os {{ sellers.items.length }} com mais valor
                        recuperado
                    </template>
                </p>
                <div
                    class="overflow-x-auto rounded-xl border border-black/5 bg-white shadow-xs dark:border-white/10 dark:bg-white/[0.02]"
                    :class="{ 'opacity-60': metricsLoading }"
                >
                    <table class="w-full min-w-[820px] text-sm">
                        <thead>
                            <tr
                                class="border-b border-black/5 dark:border-white/10"
                            >
                                <th
                                    v-for="header in sellerHeaders"
                                    :key="header.label"
                                    scope="col"
                                    class="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400"
                                    :class="header.align"
                                >
                                    {{ header.label }}
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="divide-y divide-slate-100 dark:divide-white/5"
                        >
                            <tr
                                v-for="(seller, index) in sellers.items"
                                :key="seller.producer.id"
                                class="transition-colors hover:bg-slate-50/70 dark:hover:bg-white/[0.03]"
                            >
                                <td
                                    class="w-10 px-4 py-3 text-xs font-semibold tabular-nums text-slate-400"
                                >
                                    {{ index + 1 }}
                                </td>
                                <td class="px-4 py-3">
                                    <div class="flex items-center gap-3">
                                        <span
                                            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
                                        >
                                            {{ initials(seller.producer.name) }}
                                        </span>
                                        <div class="min-w-0">
                                            <router-link
                                                :to="sellerRoute(seller)"
                                                target="_blank"
                                                v-tooltip="
                                                    'Abrir o seller em nova aba'
                                                "
                                                class="block max-w-[220px] truncate font-medium text-slate-900 hover:text-indigo-600 hover:underline dark:text-white dark:hover:text-indigo-300"
                                            >
                                                {{ seller.producer.name }}
                                            </router-link>
                                            <span
                                                class="text-xs text-slate-400"
                                            >
                                                #{{ seller.producer.id }}
                                            </span>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-4 py-3 text-right tabular-nums">
                                    <span
                                        class="block font-semibold text-slate-900 dark:text-white"
                                    >
                                        {{ formatNumber(seller.sales_orders) }}
                                    </span>
                                    <span class="block text-xs text-slate-400">
                                        {{ formatMoney(seller.sales_amount) }}
                                    </span>
                                </td>
                                <td class="px-4 py-3">
                                    <div class="flex items-center gap-2">
                                        <div
                                            class="h-1.5 w-28 overflow-hidden rounded-full bg-indigo-100 dark:bg-indigo-500/15"
                                        >
                                            <div
                                                class="h-full rounded-full bg-indigo-500 dark:bg-indigo-400"
                                                :style="{
                                                    width: `${clampPercent(seller.sales_share)}%`,
                                                }"
                                            ></div>
                                        </div>
                                        <span
                                            class="text-sm font-semibold tabular-nums text-slate-900 dark:text-white"
                                        >
                                            {{
                                                formatPercent(
                                                    seller.sales_share,
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <span
                                        class="mt-0.5 block text-xs text-slate-400"
                                    >
                                        {{
                                            formatNumber(
                                                seller.recovered_orders,
                                            )
                                        }}
                                        de
                                        {{ formatNumber(seller.sales_orders) }}
                                        vendas
                                    </span>
                                </td>
                                <td class="px-4 py-3 text-right tabular-nums">
                                    <span
                                        class="block font-semibold text-slate-900 dark:text-white"
                                    >
                                        {{
                                            formatMoney(seller.recovered_amount)
                                        }}
                                    </span>
                                    <span
                                        v-if="canSeeMoney"
                                        class="block text-xs text-slate-400"
                                    >
                                        {{ formatPercent(seller.amount_share) }}
                                        do faturamento
                                    </span>
                                </td>
                                <td class="px-4 py-3 text-right tabular-nums">
                                    <span
                                        class="block font-semibold text-slate-900 dark:text-white"
                                    >
                                        {{ formatPercent(seller.rate) }}
                                    </span>
                                    <span class="block text-xs text-slate-400">
                                        {{
                                            formatNumber(
                                                seller.recovered_orders,
                                            )
                                        }}
                                        de
                                        {{
                                            formatNumber(seller.reminded_orders)
                                        }}
                                        lembrados
                                    </span>
                                </td>
                                <td class="px-4 py-3 text-right">
                                    <button
                                        type="button"
                                        @click="showSellerRecoveries(seller)"
                                        class="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                                    >
                                        <list-bullet-icon class="h-4 w-4" />
                                        Ver pedidos
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </section>

        <section v-show="tab === 'recoveries'" aria-label="Pedidos recuperados">
            <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p class="text-sm text-slate-500 dark:text-slate-400">
                    <template v-if="recoveries.producer">
                        <strong
                            class="font-semibold text-slate-900 dark:text-white"
                        >
                            {{ formatNumber(recoveries.pagination.total) }}
                        </strong>
                        pedidos de {{ recoveries.producer.name }} pagos depois
                        do lembrete
                        <template v-if="canSeeMoney">
                            ·
                            <strong
                                class="font-semibold text-slate-900 dark:text-white"
                            >
                                {{
                                    formatMoney(
                                        recoveries.producer.recovered_amount,
                                    )
                                }}
                            </strong>
                            recuperados
                        </template>
                    </template>
                    <template v-else>
                        <strong
                            class="font-semibold text-slate-900 dark:text-white"
                        >
                            {{ formatNumber(conversion.paid_after_reminder) }}
                        </strong>
                        pedidos pagos depois do lembrete
                        <template v-if="canSeeMoney">
                            ·
                            <strong
                                class="font-semibold text-slate-900 dark:text-white"
                            >
                                {{ formatMoney(conversion.recovered_amount) }}
                            </strong>
                            recuperados
                        </template>
                    </template>
                    · últimos {{ days }} dias
                </p>
                <span
                    v-if="recoveries.producer"
                    class="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 py-1 pl-3 pr-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-400/20"
                >
                    Seller: {{ recoveries.producer.name }}
                    <button
                        type="button"
                        @click="clearSellerFilter"
                        aria-label="Remover filtro de seller"
                        class="rounded-full p-0.5 transition hover:bg-indigo-100 dark:hover:bg-indigo-500/20"
                    >
                        <x-mark-icon class="h-3.5 w-3.5" />
                    </button>
                </span>
            </div>
            <div class="w-full min-w-0 overflow-x-auto">
                <div class="min-w-max whitespace-nowrap">
                    <table-component
                        :loading="recoveries.loading"
                        :headers="[
                            'Pedido',
                            'Cliente',
                            'Produto',
                            'Lembrete e pagamento',
                            'Forma',
                            'Valor',
                            '',
                        ]"
                        :pagination="recoveries.pagination"
                        @fetch="fetchRecoveries"
                    >
                        <tr
                            v-for="row in recoveries.rows"
                            :key="row.transaction.id"
                            class="border-t border-slate-100 dark:border-slate-800"
                        >
                            <td class="table-cell-sm">
                                <router-link
                                    :to="orderRoute(row)"
                                    class="font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                                >
                                    #{{ row.order.id }}
                                </router-link>
                                <span
                                    class="block text-xs text-slate-400"
                                    v-tooltip="
                                        `transação #${row.transaction.id}`
                                    "
                                >
                                    {{
                                        row.transaction.hash ||
                                        `tx ${row.transaction.id}`
                                    }}
                                </span>
                            </td>
                            <td class="table-cell-sm">
                                <span
                                    class="block max-w-[180px] truncate text-slate-900 dark:text-white"
                                    v-tooltip="row.order.customer_name"
                                >
                                    {{ row.order.customer_name || "-" }}
                                </span>
                                <span
                                    class="block max-w-[180px] truncate text-xs text-slate-400"
                                    v-tooltip="row.order.customer_email"
                                >
                                    {{ row.order.customer_email || "-" }}
                                </span>
                            </td>
                            <td class="table-cell-sm">
                                <span
                                    class="block max-w-[200px] truncate"
                                    v-tooltip="row.order.product"
                                >
                                    {{ row.order.product || "-" }}
                                </span>
                                <span
                                    class="block max-w-[200px] truncate text-xs text-slate-400"
                                >
                                    {{ row.order.producer?.name || "-" }}
                                </span>
                            </td>
                            <td class="table-cell-sm">
                                <span class="block">{{ row.reminded_at }}</span>
                                <span class="block text-xs text-slate-400">
                                    pago {{ paidLabel(row) }} ·
                                    {{ formatMinutes(row.minutes_to_pay) }}
                                    depois
                                </span>
                            </td>
                            <td class="table-cell-sm">
                                <payment-method-icon
                                    :method="row.transaction.payment_method"
                                />
                            </td>
                            <td class="table-cell-sm tabular-nums">
                                <span
                                    class="block font-semibold text-slate-900 dark:text-white"
                                >
                                    {{ formatMoney(row.recovered_amount) }}
                                </span>
                                <span
                                    v-if="
                                        row.reminded_amount !==
                                        row.recovered_amount
                                    "
                                    class="block text-xs text-slate-400"
                                    v-tooltip="
                                        'Valor do PIX que recebeu o lembrete'
                                    "
                                >
                                    lembrado
                                    {{ formatMoney(row.reminded_amount) }}
                                </span>
                            </td>
                            <td class="table-cell-sm text-right">
                                <router-link
                                    :to="orderRoute(row)"
                                    target="_blank"
                                    v-tooltip="'Abrir o pedido em nova aba'"
                                    class="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                                >
                                    <eye-icon class="h-4 w-4" />
                                    Ver pedido
                                </router-link>
                            </td>
                        </tr>
                    </table-component>
                </div>
            </div>
        </section>

        <section v-show="tab === 'settings'" aria-label="Configurações">
            <div v-if="loading" class="grid gap-4 lg:grid-cols-3">
                <div
                    v-for="block in ['h-80 lg:col-span-2', 'h-80']"
                    :key="block"
                    :class="[panel, block, 'animate-pulse']"
                >
                    <div
                        class="h-3 w-32 rounded bg-slate-100 dark:bg-white/10"
                    ></div>
                </div>
            </div>

            <div v-else class="grid gap-4 lg:grid-cols-3">
                <div :class="[panel, 'lg:col-span-2']">
                    <h2
                        class="text-sm font-semibold text-slate-900 dark:text-white"
                    >
                        Notificações
                    </h2>
                    <p
                        class="mt-0.5 text-xs text-slate-500 dark:text-slate-400"
                    >
                        Interruptor global. Desligado aqui, o tipo não é enviado
                        para nenhum seller.
                    </p>
                    <ul
                        class="mt-2 divide-y divide-slate-100 dark:divide-white/5"
                    >
                        <li
                            v-for="type in notificationTypes"
                            :key="type.key"
                            class="flex items-center justify-between gap-4 py-3.5"
                        >
                            <div class="min-w-0">
                                <p
                                    class="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900 dark:text-white"
                                >
                                    {{ type.label }}
                                    <span
                                        class="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-normal text-slate-500 dark:bg-white/10 dark:text-slate-300"
                                    >
                                        {{ type.origin }}
                                    </span>
                                </p>
                                <p
                                    class="mt-0.5 text-xs text-slate-500 dark:text-slate-400"
                                >
                                    {{ type.description }}
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                :aria-checked="
                                    form.options.notifications[type.key]
                                        ? 'true'
                                        : 'false'
                                "
                                :aria-label="type.label"
                                :disabled="saving"
                                @click="toggleNotification(type.key)"
                                class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 dark:focus-visible:ring-offset-slate-900"
                                :class="
                                    form.options.notifications[type.key]
                                        ? 'bg-indigo-500'
                                        : 'bg-slate-200 dark:bg-white/15'
                                "
                            >
                                <span
                                    class="inline-block h-5 w-5 rounded-full bg-white shadow transition-transform"
                                    :class="
                                        form.options.notifications[type.key]
                                            ? 'translate-x-[22px]'
                                            : 'translate-x-0.5'
                                    "
                                ></span>
                            </button>
                        </li>
                    </ul>
                </div>

                <div :class="panel">
                    <h2
                        class="text-sm font-semibold text-slate-900 dark:text-white"
                    >
                        Lembrete de pagamento pendente
                    </h2>
                    <label
                        for="pending_payment_delay_minutes"
                        class="mt-4 block text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                        Enviar depois de
                    </label>
                    <div class="mt-1 flex rounded-lg shadow-xs">
                        <input
                            id="pending_payment_delay_minutes"
                            v-model.number="
                                form.options.pending_payment_delay_minutes
                            "
                            type="number"
                            min="1"
                            max="1440"
                            step="1"
                            :placeholder="String(defaultDelayMinutes)"
                            class="block w-full rounded-l-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white"
                        />
                        <span
                            class="inline-flex items-center rounded-r-lg border border-l-0 border-slate-300 bg-slate-50 px-3 text-sm text-slate-500 dark:border-white/10 dark:bg-white/10 dark:text-slate-300"
                        >
                            min
                        </span>
                    </div>
                    <p
                        class="mt-1.5 text-xs text-slate-400 dark:text-slate-500"
                    >
                        De 1 a 1440. Vale para os PIX gerados depois de salvar.
                    </p>
                    <p
                        class="mt-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                        :class="
                            reminderEnabled
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                                : 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300'
                        "
                    >
                        <span
                            class="h-1.5 w-1.5 rounded-full"
                            :class="
                                reminderEnabled
                                    ? 'bg-emerald-500'
                                    : 'bg-slate-400'
                            "
                        ></span>
                        {{
                            reminderEnabled
                                ? "Lembrete ligado"
                                : "Lembrete desligado em Notificações"
                        }}
                    </p>

                    <details
                        class="group mt-5 border-t border-slate-100 pt-3 dark:border-white/5"
                    >
                        <summary
                            class="flex cursor-pointer list-none items-center justify-between text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                            Como o lembrete funciona
                            <chevron-down-icon
                                class="h-4 w-4 transition-transform group-open:rotate-180"
                            />
                        </summary>
                        <ol class="mt-3 flex flex-col gap-2.5">
                            <li
                                v-for="(step, index) in steps"
                                :key="index"
                                class="flex gap-2.5 text-xs text-slate-500 dark:text-slate-400"
                            >
                                <span
                                    class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-[11px] font-semibold text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300"
                                >
                                    {{ index + 1 }}
                                </span>
                                <span class="pt-0.5">{{ step }}</span>
                            </li>
                        </ol>
                    </details>
                </div>

                <div
                    class="flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 lg:col-span-3"
                    :class="
                        isDirty
                            ? 'border-indigo-200 bg-indigo-50/70 dark:border-indigo-500/30 dark:bg-indigo-500/10'
                            : 'border-black/5 bg-white dark:border-white/10 dark:bg-white/[0.02]'
                    "
                >
                    <p class="text-xs text-slate-500 dark:text-slate-400">
                        <strong
                            v-if="isDirty"
                            class="font-semibold text-indigo-700 dark:text-indigo-300"
                        >
                            Alterações não salvas.
                        </strong>
                        <template v-else-if="lastChange">
                            Última alteração por
                            <strong class="font-medium">{{
                                lastChange.by
                            }}</strong>
                            em {{ lastChange.when }}
                        </template>
                        <template v-else-if="service">
                            Configuração salva, sem alterações registradas.
                        </template>
                        <template v-else>
                            Sem configuração salva: o Payments usa
                            {{ defaultDelayMinutes }} min com tudo ligado.
                        </template>
                    </p>
                    <div class="flex items-center gap-2">
                        <button
                            v-if="isDirty"
                            type="button"
                            @click="discardChanges"
                            :disabled="saving"
                            class="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10"
                        >
                            Descartar
                        </button>
                        <button
                            type="button"
                            @click="submit"
                            :disabled="saving || (!isDirty && !!service)"
                            class="inline-flex items-center rounded-md bg-indigo-500 px-4 py-1.5 text-sm font-semibold text-white shadow-xs transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {{ saving ? "Salvando..." : "Salvar" }}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { markRaw } from "vue";
import { useToast } from "vue-toastification";
import InlineEmptyState from "../../components/layouts/InlineEmptyState.vue";
import PaymentMethodIcon from "../../components/layouts/PaymentMethodIcon.vue";
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import {
    ArrowPathIcon,
    ChartBarIcon,
    ChatBubbleLeftRightIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    Cog6ToothIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    InboxIcon,
    ListBulletIcon,
    SparklesIcon,
    TableCellsIcon,
    UsersIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline";

// Mesmo default do Payments (config services.iris.pending_payment.default_delay_minutes)
const DEFAULT_DELAY_MINUTES = 10;

// Mesmos event_types do enum App\Enum\Iris\IrisNotificationType.
const NOTIFICATION_TYPES = [
    {
        key: "pix_gerado",
        label: "Pix Gerado",
        origin: "Payments",
        description: "QR Code na hora em que o PIX é gerado.",
    },
    {
        key: "pagamento_pendente",
        label: "Pagamento Pendente",
        origin: "Payments",
        description: "Lembrete com link de retomada se o PIX seguir pendente.",
    },
    {
        key: "acesso_ebook",
        label: "Acesso a E-book",
        origin: "Kraken",
        description: "Link de download quando a compra do e-book é aprovada.",
    },
    {
        key: "acesso_mozart",
        label: "Acesso ao Mozart",
        origin: "Mozart",
        description: "Primeiro acesso à área de membros.",
    },
];

const DEFAULT_NOTIFICATIONS = Object.fromEntries(
    NOTIFICATION_TYPES.map((type) => [type.key, true]),
);

const DEFAULT_OPTIONS = {
    // Chave legada lida pelo Payments antigo; mantida em sincronia com notifications.pagamento_pendente.
    pending_payment_enabled: true,
    pending_payment_delay_minutes: DEFAULT_DELAY_MINUTES,
    notifications: { ...DEFAULT_NOTIFICATIONS },
};

const PERIODS = [7, 30, 90];

const DEFAULT_PERIOD = 30;

const MONEY_MASK = "********";

const EMPTY_EVENT = { sent: 0, failed: 0, last_sent_at: null };

// Espelha IrisMetricsService::conversion(); valores em centavos.
const EMPTY_CONVERSION = {
    reminded_orders: 0,
    reminded_amount: 0,
    paid_after_reminder: 0,
    recovered_amount: 0,
    rate: 0,
    amount_rate: 0,
    average_ticket: 0,
    median_minutes_to_pay: null,
    by_payment_method: [],
};

const EMPTY_PAGINATION = {
    total: 0,
    per_page: 20,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0,
};

// A aba vai para a URL (?aba=) para o link abrir direto nela.
const TABS = [
    {
        id: "overview",
        slug: "visao-geral",
        label: "Visão geral",
        icon: markRaw(ChartBarIcon),
    },
    {
        id: "sellers",
        slug: "sellers",
        label: "Sellers",
        icon: markRaw(UsersIcon),
    },
    {
        id: "recoveries",
        slug: "recuperados",
        label: "Pedidos recuperados",
        icon: markRaw(ListBulletIcon),
    },
    {
        id: "settings",
        slug: "configuracoes",
        label: "Configurações",
        icon: markRaw(Cog6ToothIcon),
    },
];

// Cores validadas com o validador de paleta do guia de dataviz (claro e escuro):
// funil em rampa ordinal de um só matiz (indigo), formas de pagamento em cores
// categóricas fixas por entidade, na ordem pix → cartão → outros.
const ACCENT = { light: "#6366f1", dark: "#818cf8" };
const FUNNEL_COLORS = {
    light: ["#818cf8", "#6366f1", "#4338ca"],
    dark: ["#4f46e5", "#6366f1", "#a5b4fc"],
};
const PAYMENT_GROUPS = [
    {
        key: "pix",
        label: "PIX",
        methods: ["pix", "pix_recurring"],
        color: { light: "#1baf7a", dark: "#199e70" },
    },
    {
        key: "card",
        label: "Cartão",
        methods: ["credit_card", "google_pay", "apple_pay"],
        color: { light: "#2a78d6", dark: "#3987e5" },
    },
    {
        key: "other",
        label: "Outros",
        methods: null,
        color: { light: "#eb6834", dark: "#d95926" },
    },
];

const AXIS_LABEL_STYLE = { color: "#94a3b8", fontSize: "11px" };
const GRID_LINE_COLOR = "rgba(148, 163, 184, 0.14)";
const TOOLTIP_STYLE = {
    backgroundColor: "#0f172a",
    borderColor: "#334155",
    borderRadius: 8,
    shadow: false,
    style: { color: "#e2e8f0", fontSize: "12px" },
};

const PANEL =
    "rounded-xl border border-black/5 bg-white p-5 shadow-xs dark:border-white/10 dark:bg-white/[0.02]";

export default {
    name: "IrisService",
    components: {
        InlineEmptyState,
        PaymentMethodIcon,
        TableComponent,
        ArrowPathIcon,
        ChatBubbleLeftRightIcon,
        ChevronDownIcon,
        ChevronRightIcon,
        ExclamationTriangleIcon,
        EyeIcon,
        ListBulletIcon,
        SparklesIcon,
        XMarkIcon,
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            panel: PANEL,
            skeletonBlocks: [
                "h-56 lg:col-span-2",
                "h-56",
                "h-72 lg:col-span-2",
                "h-72",
            ],
            emptyIcon: markRaw(InboxIcon),
            usersIcon: markRaw(UsersIcon),
            sellerHeaders: [
                { label: "#", align: "text-left" },
                { label: "Seller", align: "text-left" },
                { label: "Vendas no período", align: "text-right" },
                { label: "Vieram do lembrete", align: "text-left" },
                { label: "Recuperado", align: "text-right" },
                { label: "Conversão do lembrete", align: "text-right" },
                { label: "", align: "text-right" },
            ],
            chartIcon: markRaw(ChartBarIcon),
            tableIcon: markRaw(TableCellsIcon),
            tabs: TABS,
            tab: "overview",
            periods: PERIODS,
            days: DEFAULT_PERIOD,
            metrics: null,
            metricsLoading: false,
            metricsError: null,
            showDailyTable: false,
            isDark: false,
            themeObserver: null,
            latest: { loading: false, rows: [] },
            recoveries: {
                loading: false,
                loadedFor: null,
                producer: null,
                rows: [],
                pagination: { ...EMPTY_PAGINATION },
            },
            loading: false,
            saving: false,
            service: null,
            savedOptions: null,
            form: {
                type: "iris",
                options: {
                    ...DEFAULT_OPTIONS,
                    notifications: { ...DEFAULT_NOTIFICATIONS },
                },
            },
            notificationTypes: NOTIFICATION_TYPES,
            defaultDelayMinutes: DEFAULT_DELAY_MINUTES,
            steps: [
                "O PIX é gerado e o Pix Gerado sai na hora, com o QR Code.",
                "Passado o tempo configurado, o Payments confere o pagamento.",
                "Se o PIX ainda estiver pendente, envia o lembrete com o link de retomada.",
                "Pagou, expirou ou o seller desligou o Iris: nada é enviado.",
            ],
        };
    },
    computed: {
        pixGenerated() {
            return this.metrics?.events?.pix_gerado ?? EMPTY_EVENT;
        },
        pendingPayment() {
            return this.metrics?.events?.pagamento_pendente ?? EMPTY_EVENT;
        },
        conversion() {
            return { ...EMPTY_CONVERSION, ...(this.metrics?.conversion ?? {}) };
        },
        daily() {
            return this.metrics?.daily ?? [];
        },
        dailyDesc() {
            return [...this.daily].reverse();
        },
        canSeeMoney() {
            return typeof this.conversion.recovered_amount === "number";
        },
        sellers() {
            return { total: 0, items: [], ...(this.metrics?.sellers ?? {}) };
        },
        sellersTop() {
            return this.sellers.items.slice(0, 5);
        },
        // O seller em que o lembrete pesou mais nas vendas, com volume mínimo para não
        // destacar quem vendeu uma vez só.
        sellerHighlight() {
            const candidates = this.sellers.items.filter(
                (seller) => seller.sales_orders >= 3,
            );

            if (!candidates.length) {
                return null;
            }

            return candidates.reduce((best, seller) =>
                seller.sales_share > best.sales_share ||
                (seller.sales_share === best.sales_share &&
                    seller.recovered_orders > best.recovered_orders)
                    ? seller
                    : best,
            );
        },
        hasActivity() {
            return (
                this.pixGenerated.sent +
                    this.pixGenerated.failed +
                    this.pendingPayment.sent +
                    this.pendingPayment.failed >
                0
            );
        },
        generatedTime() {
            return String(this.metrics?.generated_at ?? "").split(" ")[1] ?? "";
        },
        heroStats() {
            const { reminded_orders, paid_after_reminder, rate } =
                this.conversion;

            return [
                {
                    label: "Pedidos recuperados",
                    value: this.formatNumber(paid_after_reminder),
                    hint: reminded_orders
                        ? `${this.formatPercent(rate)} dos ${this.formatNumber(reminded_orders)} lembrados`
                        : "nenhum lembrete no período",
                },
                {
                    label: "Ticket médio",
                    value: this.formatMoney(this.conversion.average_ticket),
                    hint: "por pedido recuperado",
                },
                {
                    label: "Tempo até pagar",
                    value: this.formatMinutes(
                        this.conversion.median_minutes_to_pay,
                    ),
                    hint: "mediana depois do lembrete",
                },
            ];
        },
        funnel() {
            const generated = this.pixGenerated.sent;
            const reminded = this.conversion.reminded_orders;
            const paid = this.conversion.paid_after_reminder;
            const widest = Math.max(generated, reminded, paid, 1);
            const share = (part, whole) =>
                whole ? this.formatPercent((part / whole) * 100) : null;

            return [
                {
                    key: "generated",
                    label: "PIX gerados",
                    value: generated,
                    note: null,
                },
                {
                    key: "reminded",
                    label: "Receberam o lembrete",
                    value: reminded,
                    note:
                        generated && reminded <= generated
                            ? `${share(reminded, generated)} ainda estavam pendentes`
                            : null,
                },
                {
                    key: "paid",
                    label: "Pagaram depois",
                    value: paid,
                    note: reminded
                        ? `${share(paid, reminded)} dos lembrados`
                        : null,
                },
            ].map((stage) => ({
                ...stage,
                width: (stage.value / widest) * 100,
            }));
        },
        funnelColors() {
            return this.isDark ? FUNNEL_COLORS.dark : FUNNEL_COLORS.light;
        },
        paymentMix() {
            const methods = this.conversion.by_payment_method;
            const total = methods.reduce((sum, item) => sum + item.orders, 0);

            if (!total) {
                return [];
            }

            return PAYMENT_GROUPS.map((group) => {
                const items = methods.filter((item) =>
                    group.methods
                        ? group.methods.includes(item.method)
                        : !PAYMENT_GROUPS.some((other) =>
                              other.methods?.includes(item.method),
                          ),
                );
                const orders = items.reduce(
                    (sum, item) => sum + item.orders,
                    0,
                );
                const amount = items.some(
                    (item) => typeof item.recovered_amount !== "number",
                )
                    ? MONEY_MASK
                    : items.reduce(
                          (sum, item) => sum + item.recovered_amount,
                          0,
                      );

                return {
                    key: group.key,
                    label: group.label,
                    color: this.isDark ? group.color.dark : group.color.light,
                    orders,
                    amount,
                    share: (orders / total) * 100,
                };
            }).filter((group) => group.orders > 0);
        },
        paymentMixLabel() {
            return this.paymentMix
                .map(
                    (item) => `${item.label} ${this.formatPercent(item.share)}`,
                )
                .join(", ");
        },
        deliveryRows() {
            return [
                {
                    label: "Pix Gerado",
                    sent: this.pixGenerated.sent,
                    failed: this.pixGenerated.failed,
                    last: this.pixGenerated.last_sent_at,
                },
                {
                    label: "Pagamento Pendente",
                    sent: this.pendingPayment.sent,
                    failed: this.pendingPayment.failed,
                    last: this.pendingPayment.last_sent_at,
                },
            ];
        },
        deliveryStatus() {
            const failed =
                this.pixGenerated.failed + this.pendingPayment.failed;
            const total =
                failed + this.pixGenerated.sent + this.pendingPayment.sent;

            if (!failed) {
                return {
                    label: "Sem falhas",
                    icon: markRaw(CheckCircleIcon),
                    classes:
                        "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
                };
            }

            const rate = total ? (failed / total) * 100 : 0;

            return {
                label: `${this.formatNumber(failed)} falhas · ${this.formatPercent(rate)}`,
                icon: markRaw(ExclamationTriangleIcon),
                classes:
                    rate >= 5
                        ? "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300"
                        : "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
            };
        },
        chartKey() {
            return `${this.metrics?.generated_at}-${this.days}-${this.isDark}`;
        },
        dailyChart() {
            const points = this.daily;
            const money = this.canSeeMoney;
            const formatMoney = this.formatMoney;
            const formatNumber = this.formatNumber;
            const compactMoney = this.compactMoney;

            return {
                chart: {
                    type: "column",
                    backgroundColor: "transparent",
                    height: 330,
                    spacing: [8, 4, 4, 0],
                    style: { fontFamily: "inherit" },
                },
                title: { text: null },
                credits: { enabled: false },
                legend: { enabled: false },
                xAxis: {
                    categories: points.map((point) => point.label),
                    labels: { style: AXIS_LABEL_STYLE },
                    lineColor: GRID_LINE_COLOR,
                    tickLength: 0,
                    crosshair: { color: "rgba(148, 163, 184, 0.12)" },
                },
                yAxis: {
                    title: { text: null },
                    min: 0,
                    allowDecimals: false,
                    gridLineColor: GRID_LINE_COLOR,
                    labels: {
                        style: AXIS_LABEL_STYLE,
                        formatter() {
                            return money
                                ? compactMoney(this.value)
                                : formatNumber(this.value);
                        },
                    },
                },
                tooltip: {
                    ...TOOLTIP_STYLE,
                    useHTML: true,
                    formatter() {
                        const point = points[this.point.index];
                        const lines = [`<b>${point.label}</b>`];

                        if (money) {
                            lines.push(
                                `Recuperado: <b>${formatMoney(point.recovered_amount)}</b>`,
                            );
                        }

                        lines.push(
                            `Pedidos recuperados: <b>${formatNumber(point.recovered)}</b>`,
                            `Lembretes enviados: ${formatNumber(point.reminded)}`,
                        );

                        return lines.join("<br/>");
                    },
                },
                plotOptions: {
                    column: {
                        borderWidth: 0,
                        borderRadius: 3,
                        maxPointWidth: 24,
                        pointPadding: 0.08,
                        groupPadding: 0.04,
                    },
                },
                series: [
                    {
                        name: money
                            ? "Valor recuperado"
                            : "Pedidos recuperados",
                        data: points.map((point) =>
                            money ? point.recovered_amount : point.recovered,
                        ),
                        color: this.isDark ? ACCENT.dark : ACCENT.light,
                    },
                ],
            };
        },
        enabledNotificationCount() {
            return NOTIFICATION_TYPES.filter(
                (type) => this.form.options.notifications[type.key],
            ).length;
        },
        disabledNotificationCount() {
            return NOTIFICATION_TYPES.length - this.enabledNotificationCount;
        },
        notificationsStatusLabel() {
            return this.disabledNotificationCount === 1
                ? "1 notificação desligada"
                : `${this.disabledNotificationCount} notificações desligadas`;
        },
        reminderEnabled() {
            return Boolean(this.form.options.notifications.pagamento_pendente);
        },
        effectiveDelay() {
            const saved = Number(
                this.savedOptions?.pending_payment_delay_minutes,
            );

            return Number.isInteger(saved) && saved >= 1
                ? saved
                : DEFAULT_DELAY_MINUTES;
        },
        isDirty() {
            if (!this.savedOptions) {
                return false;
            }

            return (
                JSON.stringify(this.comparable(this.form.options)) !==
                JSON.stringify(this.comparable(this.savedOptions))
            );
        },
        lastChange() {
            const history = this.service?.history;

            if (!history?.when) {
                return null;
            }

            return {
                by:
                    history.performed_by?.user_name ||
                    history.performed_by?.user_email ||
                    "usuário não identificado",
                when: this.formatDateTime(history.when),
            };
        },
    },
    mounted() {
        const query = this.$route?.query ?? {};
        const fromQuery = TABS.find((item) => item.slug === query.aba);
        const days = Number(query.dias);

        if (fromQuery) {
            this.tab = fromQuery.id;
        }

        if (PERIODS.includes(days)) {
            this.days = days;
        }

        this.isDark = document.documentElement.classList.contains("dark");
        this.themeObserver = new MutationObserver(() => {
            this.isDark = document.documentElement.classList.contains("dark");
        });
        this.themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });

        this.fetch("iris");
        this.fetchMetrics();
    },
    beforeUnmount() {
        this.themeObserver?.disconnect();
    },
    methods: {
        tabCount(id) {
            if (id === "recoveries") {
                return this.conversion.paid_after_reminder;
            }

            return id === "sellers" ? this.sellers.total : null;
        },
        selectTab(id) {
            this.tab = id;
            this.syncQuery();

            if (id === "recoveries") {
                this.ensureRecoveries();
            }
        },
        changePeriod(period) {
            if (this.days === period) {
                return;
            }

            this.days = period;
            this.syncQuery();
            this.fetchMetrics();
        },
        syncQuery() {
            const slug = TABS.find((item) => item.id === this.tab)?.slug;

            this.$router
                ?.replace({
                    query: { ...this.$route.query, aba: slug, dias: this.days },
                })
                .catch(() => {});
        },
        fetch(type) {
            this.loading = true;
            axios
                .get(`/api/services/type/${type}`)
                .then((response) => {
                    // Primeiro acesso: ainda não existe linha type=iris na tabela services
                    if (response.data && Object.keys(response.data).length) {
                        this.service = response.data;
                        this.form.options = this.mergeOptions(
                            response.data.options || {},
                        );
                    }

                    this.savedOptions = this.clone(this.form.options);
                })
                .catch((error) => console.error(error))
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchMetrics(refresh = false) {
            this.metricsLoading = true;
            this.metricsError = null;
            axios
                .get(`/api/services/iris/metrics`, {
                    params: { days: this.days, refresh: refresh ? 1 : 0 },
                })
                .then(({ data }) => {
                    this.metrics = data;
                    // Cards, gráfico e listas são o mesmo retrato em cache: recarregam juntos.
                    this.recoveries.loadedFor = null;
                    this.fetchLatest();

                    if (this.tab === "recoveries") {
                        this.ensureRecoveries();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    this.metricsError =
                        error?.response?.data?.message ??
                        "Não foi possível carregar as métricas do Iris.";
                })
                .finally(() => {
                    this.metricsLoading = false;
                });
        },
        fetchLatest() {
            if (!this.conversion.paid_after_reminder) {
                this.latest.rows = [];
                return;
            }

            this.latest.loading = true;
            axios
                .get(`/api/services/iris/recoveries`, {
                    params: { days: this.days, page: 1, per_page: 5 },
                })
                .then(({ data }) => {
                    this.latest.rows = data.data;
                })
                .catch((error) => console.error(error))
                .finally(() => {
                    this.latest.loading = false;
                });
        },
        showSellerRecoveries(seller) {
            this.recoveries.producer = {
                id: seller.producer.id,
                name: seller.producer.name,
                recovered_amount: seller.recovered_amount,
            };
            this.selectTab("recoveries");
        },
        clearSellerFilter() {
            this.recoveries.producer = null;
            this.ensureRecoveries();
        },
        ensureRecoveries() {
            const key = `${this.days}:${this.metrics?.generated_at}:${this.recoveries.producer?.id ?? "all"}`;

            if (this.recoveries.loadedFor === key) {
                return;
            }

            this.recoveries.loadedFor = key;
            this.recoveries.pagination.current_page = 1;
            this.fetchRecoveries();
        },
        fetchRecoveries() {
            this.recoveries.loading = true;
            axios
                .get(`/api/services/iris/recoveries`, {
                    params: {
                        days: this.days,
                        page: this.recoveries.pagination.current_page,
                        per_page: this.recoveries.pagination.per_page,
                        producer_id: this.recoveries.producer?.id,
                    },
                })
                .then(({ data: { data: rows, ...pagination } }) => {
                    this.recoveries.rows = rows;
                    this.recoveries.pagination = {
                        ...this.recoveries.pagination,
                        ...pagination,
                    };
                })
                .catch((error) => {
                    console.error(error);
                    this.recoveries.loadedFor = null;
                    this.toast.error(
                        error?.response?.data?.message ??
                            "Não foi possível carregar os pedidos recuperados.",
                    );
                })
                .finally(() => {
                    this.recoveries.loading = false;
                });
        },
        // Ausente = ligado (mesma regra dos serviços de envio). Configuração antiga
        // sem `notifications` herda o lembrete da chave legada pending_payment_enabled.
        mergeOptions(saved) {
            const notifications = {
                ...DEFAULT_NOTIFICATIONS,
                ...(saved.notifications || {}),
            };

            if (
                !saved.notifications &&
                typeof saved.pending_payment_enabled !== "undefined"
            ) {
                notifications.pagamento_pendente = Boolean(
                    saved.pending_payment_enabled,
                );
            }

            return { ...DEFAULT_OPTIONS, ...saved, notifications };
        },
        toggleNotification(key) {
            this.form.options.notifications[key] =
                !this.form.options.notifications[key];
        },
        discardChanges() {
            this.form.options = this.clone(this.savedOptions);
        },
        comparable(options) {
            return {
                delay: Number(options?.pending_payment_delay_minutes),
                notifications: NOTIFICATION_TYPES.map((type) =>
                    Boolean(options?.notifications?.[type.key]),
                ),
            };
        },
        clone(value) {
            return JSON.parse(JSON.stringify(value));
        },
        submit() {
            const minutes = Number(
                this.form.options.pending_payment_delay_minutes,
            );

            if (!Number.isInteger(minutes) || minutes < 1 || minutes > 1440) {
                this.toast.error(
                    "Informe um número inteiro de minutos entre 1 e 1440.",
                );
                return;
            }

            this.form.options.pending_payment_delay_minutes = minutes;
            NOTIFICATION_TYPES.forEach((type) => {
                this.form.options.notifications[type.key] = Boolean(
                    this.form.options.notifications[type.key],
                );
            });
            this.form.options.pending_payment_enabled =
                this.form.options.notifications.pagamento_pendente;

            this.saving = true;

            const request = this.service
                ? axios.put(`/api/services/${this.service.id}`, this.form)
                : axios.post(`/api/services/`, this.form);

            request
                .then(({ data }) => {
                    if (data) {
                        this.toast.success(
                            "Configuração do Iris salva com sucesso",
                        );
                        this.fetch("iris");
                    }
                })
                .catch((error) => {
                    this.toast.error(
                        error?.response?.data?.message ??
                            "Não foi possível salvar a configuração do Iris.",
                    );
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        orderRoute(row) {
            return { name: "user.order", params: { order_id: row.order.id } };
        },
        sellerRoute(seller) {
            return {
                name: "user.details",
                params: { user_id: seller.producer.id },
            };
        },
        initials(name) {
            const words = String(name ?? "")
                .trim()
                .split(/\s+/)
                .filter(Boolean);

            return (
                (words[0]?.[0] ?? "") +
                (words.length > 1 ? words.at(-1)[0] : "")
            ).toUpperCase();
        },
        // "dd/mm/aaaa HH:MM" do backend: só a hora quando pagou no mesmo dia do lembrete.
        paidLabel(row) {
            const [paidDate = "", paidTime = ""] = String(
                row.paid_at ?? "",
            ).split(" ");
            const [remindedDate = ""] = String(row.reminded_at ?? "").split(
                " ",
            );

            return paidDate === remindedDate
                ? paidTime
                : `${paidDate.slice(0, 5)} ${paidTime}`;
        },
        clampPercent(value) {
            return Math.min(100, Math.max(0, Number(value) || 0));
        },
        formatNumber(value) {
            return new Intl.NumberFormat("pt-BR").format(Number(value) || 0);
        },
        formatPercent(value, digits = 1) {
            return `${new Intl.NumberFormat("pt-BR", {
                maximumFractionDigits: digits,
            }).format(Number(value) || 0)}%`;
        },
        // Centavos em R$; quem não tem role financeira recebe '********' do backend.
        formatMoney(value) {
            if (typeof value === "string" && Number.isNaN(Number(value))) {
                return value;
            }

            return this.$root.formatCurrency(value);
        },
        compactMoney(cents) {
            return new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
                notation: "compact",
                maximumFractionDigits: 1,
            }).format((Number(cents) || 0) / 100);
        },
        formatMinutes(minutes) {
            if (minutes === null || minutes === undefined) {
                return "sem dados";
            }

            const total = Math.max(0, Math.round(Number(minutes) || 0));

            if (total < 1) {
                return "< 1 min";
            }

            if (total < 60) {
                return `${total} min`;
            }

            const hours = Math.floor(total / 60);
            const rest = total % 60;

            if (hours < 24) {
                return rest
                    ? `${hours}h ${String(rest).padStart(2, "0")}min`
                    : `${hours}h`;
            }

            const days = Math.floor(hours / 24);
            const restHours = hours % 24;

            return restHours ? `${days}d ${restHours}h` : `${days}d`;
        },
        formatDateTime(value) {
            const date = new Date(value);

            if (Number.isNaN(date.getTime())) {
                return String(value);
            }

            return date.toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
    },
};
</script>
